import React, { useMemo, useState } from 'react';
import { MdOutlineStorefront, MdMyLocation } from 'react-icons/md';
import { SectionCard, Pill, Chip, StatTile, SubHeading, Note, Disclosure, Segmented } from './ui';
import ChartFrame from '../charts/ChartFrame';
import HBarList from '../charts/HBarList';
import { fmtNum, fmtPct, fmtMiles, fmtDate, fmtRatioPct, titleCase, safeArray, isNum, DASH, publicCategoryLabel, PUBLIC_PLACE_CATEGORY_ORDER } from '../format';

// Anchor catalog rt-anchor-v2 renamed `parks_recreation` → `fitness_recreation`
// (gyms, YMCAs, swim schools, Topgolf): the brand layer never held real parks,
// so the legacy key keeps the same honest label and folds into the new one.
const GROUP_LABELS = {
  daily_necessities: 'Daily needs',
  dining: 'Dining',
  retail_anchors: 'Retail',
  healthcare: 'Healthcare',
  fitness_recreation: 'Fitness & recreation',
  parks_recreation: 'Fitness & recreation',
  lifestyle: 'Lifestyle',
  mobility: 'Mobility',
  travel_infrastructure: 'Travel & lodging',
};
const LEGACY_GROUPS = { parks_recreation: 'fitness_recreation' };
const GROUP_ORDER = Object.keys(GROUP_LABELS).filter((k) => !LEGACY_GROUPS[k]);
const ANCHOR_PREVIEW = 18;
const PUBLIC_PREVIEW = 20;
const PUBLIC_UNKNOWN_ORDER = PUBLIC_PLACE_CATEGORY_ORDER.length;

export const PLACES_HEADINGS = {
  anchors: { title: 'Commercial anchors', caption: 'named brands · editorial importance' },
  publicPlaces: { title: 'Public & recreational places', caption: 'named, mappable · not scored' },
  externalities: { title: 'Adverse uses', caption: 'signed screening · distance-decayed' },
};

export function groupLabel(key) {
  return GROUP_LABELS[key] || titleCase(key);
}

/** Old and new backends both render under the current group key. */
export function canonicalGroup(key) {
  return LEGACY_GROUPS[key] || key;
}

function publicCategoryRank(key) {
  const idx = PUBLIC_PLACE_CATEGORY_ORDER.indexOf(key);
  return idx === -1 ? PUBLIC_UNKNOWN_ORDER : idx;
}

/** Group public places by category (display order), keeping nearest-first inside each group. */
export function groupPublicPlaces(places) {
  const byCategory = new Map();
  safeArray(places).forEach((p) => {
    const key = p?.category || 'other';
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(p);
  });
  return Array.from(byCategory.entries())
    .map(([key, items]) => ({ key, label: publicCategoryLabel(key), items }))
    .sort((a, b) => publicCategoryRank(a.key) - publicCategoryRank(b.key) || a.label.localeCompare(b.label));
}

export function severityTone(effective) {
  const v = Math.abs(Number(effective) || 0);
  if (v >= 40) return 'critical';
  if (v >= 15) return 'warning';
  return 'neutral';
}

function LocateButton({ onClick, label }) {
  if (!onClick) return null;
  return (
    <button type="button" className="le-locate-btn" onClick={onClick} title={`Show ${label} on the map`} aria-label={`Show ${label} on the map`}>
      <MdMyLocation size={14} />
    </button>
  );
}

function PublicPlaceRow({ place, onFocusPlace }) {
  const label = publicCategoryLabel(place.category);
  const focus = onFocusPlace && isNum(place.latitude) && isNum(place.longitude)
    ? () => onFocusPlace({ latitude: place.latitude, longitude: place.longitude, label: place.name, kind: 'public', detail: `${label} · ${fmtMiles(place.distance_miles)}` })
    : null;
  return (
    <li className="le-public-row">
      <span className="le-anchor-body">
        <span className="le-anchor-name">
          {place.name}
          <Pill tone="public" title={place.category_path || place.category}>{label}</Pill>
          {place.is_stale ? <Chip title={`Last refreshed ${fmtDate(place.date_refreshed)}`}>stale</Chip> : null}
        </span>
        <span className="le-anchor-meta">{[place.locality, place.address].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="le-anchor-distance">{fmtMiles(place.distance_miles)}</span>
      <LocateButton onClick={focus} label={place.name} />
    </li>
  );
}

function AnchorRow({ anchor, onFocusPlace }) {
  const nearest = anchor.nearest || {};
  const focus = onFocusPlace && isNum(nearest.latitude) && isNum(nearest.longitude)
    ? () => onFocusPlace({ latitude: nearest.latitude, longitude: nearest.longitude, label: anchor.name, kind: 'anchor', detail: `${groupLabel(anchor.group)} · ${fmtMiles(anchor.distance_miles)}` })
    : null;
  return (
    <li className="le-anchor-row">
      <span className={`le-tier le-tier-${(anchor.tier || 'context').replace('+', 'plus')}`} title={`Anchor score ${anchor.anchor_score ?? DASH} / 100`}>{anchor.tier || DASH}</span>
      <span className="le-anchor-body">
        <span className="le-anchor-name">
          {anchor.name}
          {anchor.locations_within_radius > 1 ? <Chip title="Locations within the radius (counted once)">×{anchor.locations_within_radius}</Chip> : null}
          {nearest.is_stale ? <Chip title={`Last refreshed ${fmtDate(nearest.date_refreshed)}`}>stale</Chip> : null}
        </span>
        <span className="le-anchor-meta">{[titleCase(anchor.subtype), nearest.locality].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="le-anchor-distance">{fmtMiles(anchor.distance_miles)}</span>
      <LocateButton onClick={focus} label={anchor.name} />
    </li>
  );
}

function ExternalityRow({ item, onFocusPlace }) {
  const tone = severityTone(item.effective_impact_score);
  const pct = item.base_impact_score ? Math.min(100, Math.abs(item.effective_impact_score / item.base_impact_score) * 100) : 0;
  const focus = onFocusPlace && isNum(item.latitude) && isNum(item.longitude)
    ? () => onFocusPlace({ latitude: item.latitude, longitude: item.longitude, label: item.name, kind: 'externality', detail: `${item.display_name || titleCase(item.type)} · ${fmtMiles(item.distance_miles)} · ${item.effective_impact_score}` })
    : null;
  return (
    <li className={`le-ext-row le-ext-${tone}`}>
      <span className="le-ext-score" title={`Base ${item.base_impact_score} decayed linearly to ${item.effective_impact_score} at ${fmtMiles(item.distance_miles)} (max ${item.max_effect_radius_miles} mi)`}>
        <strong>{isNum(item.effective_impact_score) ? item.effective_impact_score.toFixed(0) : DASH}</strong>
        <span className="le-ext-base">of {item.base_impact_score}</span>
      </span>
      <span className="le-ext-body">
        <span className="le-ext-name">
          {item.name}
          <Pill tone={tone}>{item.display_name || titleCase(item.type)}</Pill>
          {item.is_stale ? <Chip title={`Last refreshed ${fmtDate(item.date_refreshed)}`}>stale</Chip> : null}
        </span>
        <span className="le-ext-meta">
          {[fmtMiles(item.distance_miles), item.locality, isNum(item.match_confidence) ? `match ${fmtRatioPct(item.match_confidence)}` : null, item.date_refreshed ? `refreshed ${fmtDate(item.date_refreshed)}` : null].filter(Boolean).join(' · ')}
        </span>
        <span className="le-ext-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
      </span>
      <LocateButton onClick={focus} label={item.name} />
    </li>
  );
}

/**
 * `data` is the output of `selectPlacesLayers(result)` — never a raw document —
 * so the split 2.2.0 datasets and the deprecated `nearby_places` umbrella render
 * through the same code. Each layer is optional: absent or excluded layers simply
 * do not render a section.
 */
export default function NearbyPlacesCard({ data, status, radiusMiles, onFocusPlace }) {
  const [group, setGroup] = useState('all');
  const [showAllAnchors, setShowAllAnchors] = useState(false);
  const [publicCategory, setPublicCategory] = useState('all');
  const [showAllPublic, setShowAllPublic] = useState(false);
  const present = Boolean(data?.hasAny);
  const scope = data?.scope || {};
  const hasAnchors = Array.isArray(data?.anchors);
  const anchors = useMemo(() => safeArray(data?.anchors), [data]);
  const scored = useMemo(() => anchors.filter((a) => a.tier !== 'context'), [anchors]);
  const context = useMemo(() => anchors.filter((a) => a.tier === 'context'), [anchors]);
  const hasExternalities = Array.isArray(data?.externalities);
  const externalities = safeArray(data?.externalities);
  const anchorSummary = data?.anchorSummary || {};
  const extSummary = data?.externalitySummary || {};
  const quality = data?.dataQuality || {};
  const notes = safeArray(data?.notes);
  const placesRadius = scope.radius_miles ?? radiusMiles;
  const scanRadius = data?.externalityScope?.scan_radius_miles ?? scope.externality_scan_radius_miles ?? 5;

  const groupsPresent = useMemo(() => GROUP_ORDER.filter((g) => scored.some((a) => canonicalGroup(a.group) === g)), [scored]);
  const filtered = useMemo(() => (group === 'all' ? scored : scored.filter((a) => canonicalGroup(a.group) === group)), [scored, group]);
  const shownAnchors = showAllAnchors ? filtered : filtered.slice(0, ANCHOR_PREVIEW);
  const topTier = (anchorSummary.by_tier?.['A+'] || 0) + (anchorSummary.by_tier?.A || 0);

  // Named public places. Absent (older backend) or excluded → the section is simply not rendered.
  const hasPublicPlaces = Array.isArray(data?.publicPlaces);
  const publicPlaces = useMemo(() => safeArray(data?.publicPlaces), [data]);
  const publicSummary = data?.publicSummary || {};
  const publicGroups = useMemo(() => groupPublicPlaces(publicPlaces), [publicPlaces]);
  const publicFiltered = useMemo(
    () => (publicCategory === 'all' ? publicPlaces : publicPlaces.filter((p) => (p?.category || 'other') === publicCategory)),
    [publicPlaces, publicCategory],
  );
  const publicShown = useMemo(() => groupPublicPlaces(showAllPublic ? publicFiltered : publicFiltered.slice(0, PUBLIC_PREVIEW)), [publicFiltered, showAllPublic]);
  const publicCounts = useMemo(() => {
    const byCategory = data?.publicSummary?.by_category || {};
    return Object.keys(byCategory)
      .map((key) => ({ key, label: publicCategoryLabel(key), count: byCategory[key]?.places, nearest: byCategory[key]?.nearest }))
      .filter((c) => isNum(c.count))
      .sort((a, b) => b.count - a.count || publicCategoryRank(a.key) - publicCategoryRank(b.key));
  }, [data]);
  const publicTotal = isNum(publicSummary.places) ? publicSummary.places : publicPlaces.length;
  const publicCapped = isNum(publicSummary.cap) && publicTotal >= publicSummary.cap;
  const publicRadius = publicSummary.radius_miles ?? data?.publicScope?.radius_miles ?? placesRadius;

  const familyNote = data?.families?.note || null;
  const families = useMemo(() => {
    const counts = data?.families?.families || {};
    const labels = data?.families?.family_labels || {};
    return Object.keys(counts)
      .map((key) => ({ key, label: labels[key] || titleCase(key), count: counts[key] }))
      .filter((f) => isNum(f.count))
      .sort((a, b) => b.count - a.count);
  }, [data]);
  const familyTop = families.filter((f) => f.count > 0).slice(0, 12);

  const subtitle = present
    ? [
      `${placesRadius ?? DASH} mi`,
      data.dataQuality ? `${fmtNum(quality.open_places)} open places` : null,
      `Foursquare release ${scope.source_release || DASH}`,
      `${scope.anchor_catalog?.methodology_version || scope.catalog?.methodology_version || 'rt-anchor'} / ${scope.externality_catalog?.methodology_version || 'rt-externality'}`,
      data.mode === 'nearby_places' ? 'via nearby_places (deprecated)' : null,
    ].filter(Boolean).join(' · ')
    : null;

  return (
    <SectionCard id="le-section-places" icon={<MdOutlineStorefront size={20} />} title="Nearby places" subtitle={subtitle} status={status} dataset={present ? data : null}>
      {present ? (
        <>
          <div className="le-stat-grid">
            <StatTile label={PLACES_HEADINGS.anchors.title} value={hasAnchors ? fmtNum(anchorSummary.scored_entities) : DASH} sub={hasAnchors ? `${fmtNum(anchorSummary.entities)} named operators incl. context · ${fmtNum(anchorSummary.places)} locations` : 'Not in this document'} emphasis />
            <StatTile label="Top-tier anchors" value={hasAnchors ? fmtNum(topTier) : DASH} sub="A+ and A: major regional or daily-needs draws" />
            <StatTile label={PLACES_HEADINGS.publicPlaces.title} value={hasPublicPlaces ? fmtNum(publicTotal) : DASH} sub={hasPublicPlaces ? `${fmtNum(publicCounts.length)} categories · nearest ${publicPlaces[0] ? `${publicPlaces[0].name} (${fmtMiles(publicPlaces[0].distance_miles)})` : DASH}` : 'Not in this document'} />
            <StatTile label={PLACES_HEADINGS.externalities.title} value={hasExternalities ? fmtNum(extSummary.facilities) : DASH} sub={hasExternalities ? (extSummary.strongest ? `Strongest: ${extSummary.strongest.name} (${extSummary.strongest.effective_impact_score})` : `None within rule radius (${scanRadius} mi scan)`) : 'Not in this document'} />
            <StatTile label="Screening total" value={isNum(extSummary.capped_effective_total) ? extSummary.capped_effective_total.toFixed(0) : DASH} sub="Sum of the strongest 5 facilities, distance-decayed" hint="A transparent screening signal — not a finding of contamination or a value adjustment" />
            <StatTile label="Stale records" value={fmtRatioPct(quality.stale_place_share)} sub={data.dataQuality ? `${fmtNum(quality.stale_places)} not refreshed in 2+ yrs` : 'Reported with nearby_places'} />
          </div>

          {hasPublicPlaces ? (
            <>
              <SubHeading caption={PLACES_HEADINGS.publicPlaces.caption} right={publicGroups.length > 1 ? (
                <Segmented
                  options={[{ value: 'all', label: 'All' }, ...publicGroups.map((g) => ({ value: g.key, label: g.label }))]}
                  value={publicGroups.some((g) => g.key === publicCategory) || publicCategory === 'all' ? publicCategory : 'all'}
                  onChange={setPublicCategory}
                  ariaLabel="Public place category"
                />
              ) : null}>
                {PLACES_HEADINGS.publicPlaces.title} · {fmtNum(publicTotal)} within {publicRadius} mi
              </SubHeading>
              {publicCounts.length ? (
                <div className="le-chip-row le-public-counts" aria-label="Public places by category">
                  {publicCounts.map((c) => (
                    <Chip key={c.key} title={c.nearest?.name ? `Nearest: ${c.nearest.name} (${fmtMiles(c.nearest.distance_miles)})` : undefined}>{`${c.label} · ${fmtNum(c.count)}`}</Chip>
                  ))}
                </div>
              ) : null}
              {publicShown.length ? (
                <>
                  <ul className="le-public-list">
                    {publicShown.map((g) => (
                      <li key={g.key} className="le-public-group">
                        {publicCategory === 'all' ? <div className="le-public-group-title">{g.label} <span className="le-public-group-count">{g.items.length}</span></div> : null}
                        <ul className="le-public-list">
                          {g.items.map((p) => <PublicPlaceRow key={p.place_key ?? p.place_id ?? `${p.name}-${p.distance_miles}`} place={p} onFocusPlace={onFocusPlace} />)}
                        </ul>
                      </li>
                    ))}
                  </ul>
                  {publicFiltered.length > PUBLIC_PREVIEW ? (
                    <button type="button" className="le-link-btn" onClick={() => setShowAllPublic((v) => !v)}>
                      {showAllPublic ? 'Show fewer' : `Show all ${publicFiltered.length} places`}
                    </button>
                  ) : null}
                </>
              ) : (
                <Note tone="muted">No named parks or public places within this radius. Try a wider places radius.</Note>
              )}
              <div className="le-footnotes muted">
                Nearest first{publicCapped ? ` · capped at the ${publicSummary.cap} nearest` : ''}{publicSummary.method ? ` · ${publicSummary.method}` : ''}. Named parks, playgrounds, pools, trails and civic places from the places source; not scored. Commercial anchors are a separate layer.
              </div>
            </>
          ) : null}

          {hasAnchors ? (
            <>
              <SubHeading caption={PLACES_HEADINGS.anchors.caption} right={groupsPresent.length > 1 ? (
                <Segmented
                  options={[{ value: 'all', label: 'All' }, ...groupsPresent.map((g) => ({ value: g, label: groupLabel(g) }))]}
                  value={groupsPresent.includes(group) || group === 'all' ? group : 'all'}
                  onChange={setGroup}
                  ariaLabel="Anchor group"
                />
              ) : null}>
                {PLACES_HEADINGS.anchors.title} within {data?.anchorScope?.radius_miles ?? placesRadius} mi
              </SubHeading>
              {filtered.length ? (
                <>
                  <ul className="le-anchor-list">
                    {shownAnchors.map((a) => <AnchorRow key={a.anchor_entity_id} anchor={a} onFocusPlace={onFocusPlace} />)}
                  </ul>
                  {filtered.length > ANCHOR_PREVIEW ? (
                    <button type="button" className="le-link-btn" onClick={() => setShowAllAnchors((v) => !v)}>
                      {showAllAnchors ? 'Show fewer' : `Show all ${filtered.length} brands`}
                    </button>
                  ) : null}
                </>
              ) : (
                <Note tone="muted">No scored commercial anchors within this radius. Try a wider places radius.</Note>
              )}
              {context.length ? (
                <Disclosure summary={`${context.length} context operator${context.length === 1 ? '' : 's'} — named, but limited standalone signal`}>
                  <ul className="le-anchor-list le-anchor-list-context">
                    {context.map((a) => <AnchorRow key={a.anchor_entity_id} anchor={a} onFocusPlace={onFocusPlace} />)}
                  </ul>
                </Disclosure>
              ) : null}
            </>
          ) : null}

          {hasExternalities ? (
            <>
              <SubHeading caption={PLACES_HEADINGS.externalities.caption}>{PLACES_HEADINGS.externalities.title} · strongest rule per facility, {scanRadius} mi scan</SubHeading>
              {externalities.length ? (
                <ul className="le-ext-list">
                  {externalities.map((e) => <ExternalityRow key={e.place_key || `${e.name}-${e.distance_miles}`} item={e} onFocusPlace={onFocusPlace} />)}
                </ul>
              ) : (
                <div className="le-inline-ok">No tagged adverse uses within their effect radius of this point.</div>
              )}
              <Note tone="muted">
                Scores are an editorial screening signal with linear distance decay ({extSummary.cap_rule ? 'capped total = strongest five facilities' : 'capped total'}). Not a finding of contamination, a regulatory determination, or an appraisal adjustment. Rules cover chemical plants, waste, power plants, prisons, factories, industrial estates, recycling, adult uses and cemeteries; airports, rail yards, military bases and quarries are not yet scored.
              </Note>
            </>
          ) : null}

          {data.families ? (
            <>
              <SubHeading>General amenities within {placesRadius} mi</SubHeading>
              {familyTop.length ? (
                <ChartFrame
                  subtitle="Open places by category family · the long tail the anchor index does not cover"
                  table={{
                    columns: [
                      { key: 'label', label: 'Family' },
                      { key: 'count', label: 'Open places', align: 'right', format: (v) => fmtNum(v) },
                    ],
                    rows: families,
                  }}
                  footnote={familyNote}
                >
                  <HBarList rows={familyTop.map((f) => ({ label: f.label, values: [f.count] }))} series={[{ id: 'places', label: 'Open places', color: 'var(--le-s1)' }]} valueFormat={(v) => fmtNum(v)} />
                </ChartFrame>
              ) : (
                <Note tone="muted">No categorized places within this radius.</Note>
              )}
            </>
          ) : null}

          {data.dataQuality ? (
            <div className="le-footnotes muted">
              {fmtNum(quality.places_within_radius)} places within radius · {fmtNum(quality.closed_places)} closed · {fmtNum(quality.flagged_places)} flagged · {fmtPct((quality.stale_place_share || 0) * 100, 0)} stale ({scope.stale_definition || 'refreshed 2+ years ago'})
              {scope.anchor_catalog?.source ? ` · anchors from ${scope.anchor_catalog.source} ${scope.anchor_catalog.source_version || ''}` : ''}
            </div>
          ) : null}
          {notes.length ? <div className="le-footnotes">{notes.map((n) => <div key={n}>{n}</div>)}</div> : null}
        </>
      ) : null}
    </SectionCard>
  );
}
